// src/pages/academy/AcademySkillsList.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Plus,
  BookOpen,
  Pencil,
  Trash2,
  ChevronRight,
  Loader2,
  ArrowRight,
  Layers,
  RefreshCw,
} from "lucide-react";
import { academySkillsAPI, getApiError } from "../../services/academyService";
import { ACADEMY_ROOT, SKILL_TYPE_LABELS } from "./academyConstants";
import AcademyDeleteModal from "./components/AcademyDeleteModal";
import MergeMixedModal from "./components/MergeMixedModal";

export default function AcademySkillsList() {
  const navigate = useNavigate();
  const { examId } = useParams();

  const [category, setCategory] = useState(null);
  const [exam, setExam] = useState(null);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [mergeModal, setMergeModal] = useState(null); // { target: skill | null }
  const [notice, setNotice] = useState("");

  useEffect(() => {
    fetchData();
  }, [examId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await academySkillsAPI.getByExam(examId);
      setCategory(data.category); // { id, name }
      setExam(data.exam); // { id, title }
      setSkills(data.skills || []);
    } catch (err) {
      console.error(err);
      setError(getApiError(err, "تعذر تحميل المهارات"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      setDeleting(true);
      setDeleteError("");
      await academySkillsAPI.delete(deleteModal.id);
      setSkills((prev) => prev.filter((s) => s.id !== deleteModal.id));
      setDeleteModal(null);
    } catch (err) {
      console.error(err);
      setDeleteError(getApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  // مهارات ميكسيد أصلية (مش المجمّعة نفسها)
  const mergeCandidates = skills.filter(
    (s) => s.skill_type === "MIXED" && !s.is_merged
  );

  const handleMergeDone = (data) => {
    setMergeModal(null);
    setNotice(`${data.message} (تم نسخ ${data.total_copied} عنصر)`);
    fetchData();
  };

  const createPath = `${ACADEMY_ROOT}/exams/${examId}/skills/create`;
  const backPath = category
    ? `${ACADEMY_ROOT}/categories/${category.id}/exams`
    : ACADEMY_ROOT;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(backPath)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowRight className="w-5 h-5 text-gray-600" />
          </button>
          <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{exam?.title}</h1>
            <p className="text-sm text-gray-500">
              {category?.name} · {skills.length} Skills
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {mergeCandidates.length > 0 && (
            <button
              onClick={() => setMergeModal({ target: null })}
              className="flex items-center gap-2 border border-indigo-200 text-indigo-700 bg-indigo-50 px-4 py-2.5 rounded-xl hover:bg-indigo-100 transition-colors font-medium"
            >
              <Layers className="w-4 h-4" />
              Merge Mixed
            </button>
          )}
          <button
            onClick={() => navigate(createPath)}
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-xl hover:bg-indigo-700 transition-colors font-medium"
          >
            <Plus className="w-4 h-4" />
            Add Skill
          </button>
        </div>
      </div>

      {notice && (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm flex items-center justify-between">
          <span>{notice}</span>
          <button
            onClick={() => setNotice("")}
            className="font-medium underline"
          >
            إغلاق
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchData} className="font-medium underline">
            Retry
          </button>
        </div>
      )}

      {skills.length === 0 && !error ? (
        <div className="text-center py-16 text-gray-400">
          <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No skills in this exam yet</p>
          <button
            onClick={() => navigate(createPath)}
            className="mt-4 inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
          >
            <Plus className="w-4 h-4" />
            Add a skill now
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {skills.map((skill) => {
            const typeInfo = SKILL_TYPE_LABELS[skill.skill_type] || {
              label: skill.skill_type,
              color: "bg-gray-100 text-gray-700",
            };
            return (
              <div
                key={skill.id}
                className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-all hover:border-indigo-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        navigate(
                          `${ACADEMY_ROOT}/exams/${examId}/skills/${skill.id}/edit`
                        )
                      }
                      className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      aria-label="Edit skill"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setDeleteError("");
                        setDeleteModal(skill);
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      aria-label="Delete skill"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    {!skill.is_active && (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
                        Inactive
                      </span>
                    )}
                    {skill.is_merged && (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-purple-100 text-purple-700">
                        Merged
                      </span>
                    )}
                    <span
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full ${typeInfo.color}`}
                    >
                      {typeInfo.label}
                    </span>
                  </div>
                </div>

                <h3 className="font-bold text-gray-900 text-right mb-1">
                  {skill.title}
                </h3>
                {skill.description && (
                  <p className="text-sm text-gray-500 text-right mb-3 line-clamp-2">
                    {skill.description}
                  </p>
                )}

                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <button
                    onClick={() =>
                      navigate(`${ACADEMY_ROOT}/skills/${skill.id}`)
                    }
                    className="flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                  >
                    <ChevronRight className="w-4 h-4" />
                    Manage Questions
                  </button>
                  <span className="text-xs text-gray-400">
                    {skill.total_questions || 0} Questions
                  </span>
                </div>

                {skill.is_merged && mergeCandidates.length > 0 && (
                  <button
                    onClick={() => setMergeModal({ target: skill })}
                    className="mt-3 w-full flex items-center justify-center gap-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-xl py-2 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    تحديث المهارة المجمّعة
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {deleteModal && (
        <AcademyDeleteModal
          title="Delete Skill"
          itemName={deleteModal.title}
          warning="Questions attached to this skill may be affected."
          deleting={deleting}
          error={deleteError}
          onCancel={() => setDeleteModal(null)}
          onConfirm={handleDelete}
        />
      )}

      {mergeModal && (
        <MergeMixedModal
          examId={examId}
          candidates={mergeCandidates}
          target={mergeModal.target}
          onClose={() => setMergeModal(null)}
          onDone={handleMergeDone}
        />
      )}
    </div>
  );
}